import React, { useState, useMemo } from 'react';
import { OpportunityItem, TechnicalStatus, OpportunityStatus } from '../models/opportunity';
import { OpportunityCard } from './OpportunityCard';
import { NovaOportunidadeModal, NovaOportunidadeForm } from './NovaOportunidadeModal';

interface Membro { nome: string; email: string; }

type Filter = 'technical' | 'mine' | 'all' | 'won';

interface PipelineBoardProps {
  opportunities: OpportunityItem[];
  currentUserEmail: string;
  currentUserName: string;
  onUpdateReview: (id: string, status: TechnicalStatus, parecer: string, historicoActual: string) => Promise<void>;
  onCreateOpportunity: (form: NovaOportunidadeForm) => Promise<void>;
  onAdvanceStatus: (id: string, newStatus: OpportunityStatus) => Promise<void>;
  getMembers: () => Promise<Membro[]>;
  getNextCode: () => Promise<string>;
}

export const PipelineBoard: React.FC<PipelineBoardProps> = ({
  opportunities, currentUserEmail, currentUserName,
  onUpdateReview, onCreateOpportunity, onAdvanceStatus,
  getMembers, getNextCode
}) => {
  const [filter, setFilter] = useState<Filter>('technical');
  const [search, setSearch] = useState('');
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [showNova, setShowNova] = useState(false);

  const isSlaCritical = (d?: string) => !!d && (new Date(d).getTime() - Date.now()) / 3600000 <= 48;

  const filtered = useMemo(() => {
    let list = opportunities;
    if (filter === 'technical') {
      list = list.filter(o =>
        o.statusOportunidade === '2. Viabilidade Técnica Pendente' &&
        o.statusTecnico !== 'Parecer Técnico Concluído'
      );
    } else if (filter === 'mine') {
      list = list.filter(o =>
        o.comercialEmail?.toLowerCase() === currentUserEmail.toLowerCase() ||
        o.responsavelTecnicoEmail?.toLowerCase() === currentUserEmail.toLowerCase()
      );
    } else if (filter === 'won') {
      list = list.filter(o => o.statusOportunidade === '5. Ganho / Aceite');
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o => o.codigoOportunidade.toLowerCase().includes(q) || o.cliente.toLowerCase().includes(q));
    }
    if (urgentOnly) list = list.filter(o => isSlaCritical(o.dataLimiteParecer));
    return list;
  }, [opportunities, filter, search, urgentOnly, currentUserEmail]);

  const pending = opportunities.filter(o =>
    o.statusOportunidade === '2. Viabilidade Técnica Pendente' &&
    o.statusTecnico !== 'Parecer Técnico Concluído'
  ).length;

  const won = opportunities.filter(o => o.statusOportunidade === '5. Ganho / Aceite').length;

  const tabStyle = (t: Filter): React.CSSProperties => ({
    padding: '14px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    color: filter === t ? 'var(--chk-white)' : 'var(--chk-muted)',
    background: 'none', border: 'none', borderBottomWidth: 2, borderBottomStyle: 'solid',
    borderBottomColor: filter === t ? (t === 'won' ? '#22C55E' : 'var(--chk-red)') : 'transparent',
    display: 'flex', alignItems: 'center', gap: 7, transition: 'all .15s',
  });

  const badge = (n: number, color: string) => n > 0 ? (
    <span style={{ background: color, color: 'white', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>{n}</span>
  ) : null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--chk-dark)' }}>
      {showNova && (
        <NovaOportunidadeModal
          currentUserEmail={currentUserEmail}
          currentUserName={currentUserName}
          onSubmit={onCreateOpportunity}
          onClose={() => setShowNova(false)}
          getMembers={getMembers}
          getNextCode={getNextCode}
        />
      )}

      {/* Tabs + Actions */}
      <div style={{ padding: '0 24px', background: 'var(--chk-navy)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--chk-border)' }}>
        <div style={{ display: 'flex' }}>
          <button onClick={() => setFilter('technical')} style={tabStyle('technical')}>
            🔧 Fila da Técnica {badge(pending, '#CC2936')}
          </button>
          <button onClick={() => setFilter('mine')} style={tabStyle('mine')}>
            👤 Minhas Oportunidades
          </button>
          <button onClick={() => setFilter('all')} style={tabStyle('all')}>
            📋 Todas
          </button>
          <button onClick={() => setFilter('won')} style={tabStyle('won')}>
            🏆 Ganhas {badge(won, '#22C55E')}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowNova(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: 'var(--chk-red)', color: 'white', border: 'none', cursor: 'pointer' }}>
            ➕ Nova Oportunidade
          </button>
        </div>
      </div>

      {/* Search + Filters */}
      <div style={{ padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--chk-dark)' }}>
        <div style={{ flex: 1, maxWidth: 320, background: 'var(--chk-card)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--chk-muted)' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar por código, cliente..."
            style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--chk-text)', fontSize: 13, width: '100%' }} />
        </div>
        {filter !== 'won' && (
          <div onClick={() => setUrgentOnly(!urgentOnly)} style={{
            display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            background: urgentOnly ? 'rgba(204,41,54,.08)' : 'var(--chk-card)',
            border: `1px solid ${urgentOnly ? 'var(--chk-red)' : 'var(--chk-border)'}`,
            padding: '7px 12px', borderRadius: 8, fontSize: 12,
            color: urgentOnly ? 'var(--chk-red)' : 'var(--chk-muted)',
          }}>⚡ Urgentes</div>
        )}
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--chk-muted)' }}>
          {filtered.length} oportunidade{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 16, padding: '4px 24px 24px' }}>
        {filtered.length === 0 ? (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 60, color: 'var(--chk-muted)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{filter === 'won' ? '🏆' : '📭'}</div>
            <div style={{ fontSize: 16 }}>
              {filter === 'won' ? 'Ainda não há oportunidades ganhas' : 'Sem oportunidades neste filtro'}
            </div>
          </div>
        ) : (
          filtered.map(item => (
            <OpportunityCard
              key={item.id}
              item={item}
              currentUserEmail={currentUserEmail}
              onUpdate={onUpdateReview}
              onAdvanceStatus={onAdvanceStatus}
            />
          ))
        )}
      </div>
    </div>
  );
};
